import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Divider,
  IconButton,
  Grid,
  Autocomplete,
  Menu,
  MenuItem,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { toast } from "react-toastify";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../utils/constants";
import { getIPDDischargePatientApi, exportIPDDischargePatientApi } from "../services/reports.service";

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)", // Background color
  width: "44px", // Width
  height: "44px", // Height
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

// Proper dropdown options
const templateOptions = [
  { label: "All IPD Discharge Reports", value: "all_ipd_discharge" },
  { label: "Patient Discharge Summary", value: "patient_discharge_summary" },
  { label: "Discharge by Date Range", value: "discharge_date_range" },
  { label: "Discharge by Department", value: "discharge_department" },
];

/* Mock tab series (retired from active path — wire dedicated APIs before restoring):
const genderWiseData = [
  { _id: "1", gender: "Male", count: "45", percentage: "65%", avgStay: "5.2 days" },
  { _id: "2", gender: "Female", count: "24", percentage: "35%", avgStay: "4.8 days" },
];
const unitWiseData = [
  { _id: "1", unit: "General Ward", discharges: "35", avgStay: "4.5 days" },
  { _id: "2", unit: "ICU", discharges: "12", avgStay: "7.2 days" },
  { _id: "3", unit: "CCU", discharges: "8", avgStay: "6.8 days" },
  { _id: "4", unit: "Private Room", discharges: "14", avgStay: "3.9 days" },
];
const sponsorWiseData = [
  { _id: "1", sponsor: "CGHS", count: "25", amount: "₹5,50,000", percentage: "36%" },
  { _id: "2", sponsor: "Insurance", count: "18", amount: "₹3,80,000", percentage: "26%" },
  { _id: "3", sponsor: "Cash", count: "15", amount: "₹2,90,000", percentage: "22%" },
  { _id: "4", sponsor: "Corporate", count: "11", amount: "₹2,10,000", percentage: "16%" },
];
*/

export default function ReportsIPDDischargePatient() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    searchTemplate: null,
    dischargeDate: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors
  const [reportData, setReportData] = useState([]); // Real-time API data for Tab 1
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Column visibility state for Tab 1 (IPD Discharge Patient Report)
  const [visibleColumns, setVisibleColumns] = useState({
    serialNumber: true,
    patientId: true,
    age: true,
    sex: true,
    address: true,
  });
  
  const handleBack = () => {
    navigate(-1); // Go back to the previous page
  };
  const [activeTab, setActiveTab] = useState(1);
  const handleTabClick = (index) => {
    setActiveTab(index);
    setPage(0); // Reset pagination when switching tabs
  };

  // Menu anchor states for download menu
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  // Menu anchor states for filter/column visibility menu
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);

  // Handle filter icon click (column visibility)
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle share functionality
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'IPD Discharge Patient Report',
        text: 'IPD Discharge Patient Report',
        url: window.location.href,
      }).catch((error) => {
        console.log('Error sharing:', error);
      });
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Report URL copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy URL');
      });
    }
  };

  // Handle download menu
  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleMenuClick = (item) => {
    handleDownloadClose();
    switch (item) {
      case 'Excel':
        handleExport();
        break;
      case 'PDF':
        toast.info('PDF export feature coming soon');
        break;
      case 'Report Mail Send Scheduler':
        toast.info('Report Mail Send Scheduler feature coming soon');
        break;
      case 'Report Scheduler':
        toast.info('Report Scheduler feature coming soon');
        break;
      default:
        break;
    }
  };

  const fetchReportData = async (currentPage = page, currentRowsPerPage = rowsPerPage) => {
    try {
      setIsLoading(true);

      const params = {
        dischargeDate: formData.dischargeDate ? formData.dischargeDate.toISOString() : undefined,
        template: formData.searchTemplate?.value,
        page: currentPage + 1,
        limit: currentRowsPerPage,
      };

      const response = await getIPDDischargePatientApi(params);
      console.log('IPD Discharge API response:', response);

      let dataArray = [];
      if (response?.data?.data) {
        dataArray = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (response?.data && Array.isArray(response.data)) {
        dataArray = response.data;
      }

      // Transform data to match table columns
      const transformedData = dataArray.map((item, index) => ({
        id: item._id || item.id || index,
        serialNumber: currentPage * currentRowsPerPage + index + 1,
        patientId: item.patientId || item.uhid || item.patientUHID || '-',
        age: item.age || '-',
        sex: item.sex || item.gender || '-',
        address: item.address || '-',
      }));

      setReportData(transformedData);
      setTotalCount(response?.data?.total || transformedData.length);
      setHasSearched(true);

      if (transformedData.length > 0) {
        toast.success("Report data loaded successfully");
      } else {
        toast.info("No data found for the selected criteria");
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setReportData([]);
      setTotalCount(0);
      setHasSearched(true);
      toast.error(error?.message || "Failed to load report data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.searchTemplate) {
      errorFields.push('searchTemplate');
    }
    if (!formData.dischargeDate) {
      errorFields.push('dischargeDate');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    setPage(0);
    
    fetchReportData(0, rowsPerPage);
  };

  const handleExport = async () => {
    if (!hasSearched) {
      toast.warning("Please search for data before exporting");
      return;
    }
    
    setIsExporting(true);
    
    try {
      const params = {
        dischargeDate: formData.dischargeDate ? formData.dischargeDate.toISOString() : undefined,
        template: formData.searchTemplate?.value,
      };

      // Build column order from visible columns
      const columnOrder = [];
      if (visibleColumns.serialNumber) columnOrder.push({ key: 'serialNumber', header: 'SNO' });
      if (visibleColumns.patientId) columnOrder.push({ key: 'patientId', header: 'Patient Id' });
      if (visibleColumns.age) columnOrder.push({ key: 'age', header: 'Age' });
      if (visibleColumns.sex) columnOrder.push({ key: 'sex', header: 'Sex' });
      if (visibleColumns.address) columnOrder.push({ key: 'address', header: 'Address' });

      await exportIPDDischargePatientApi({ ...params, columnOrder });
      toast.success("Report exported successfully");
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error(error?.message || "Failed to export report");
    } finally {
      setIsExporting(false);
    }
  };

  // Get current tab's data and total count for pagination
  const getCurrentTabData = () => {
    if (!hasSearched) return [];
    switch (activeTab) {
      case 1:
        return reportData;
      case 2:
      case 3:
      case 4:
        /* return genderWiseData / unitWiseData / sponsorWiseData; — pending API */
        return [];
      default:
        return [];
    }
  };

  const currentData = getCurrentTabData();
  const paginatedData = currentData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <Paper sx={{ mt: 2 }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      );
    }

    if (!hasSearched) {
      return (
        <Paper sx={{ mt: 2 }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      );
    }

    switch (activeTab) {
      case 1:
        return (
          <Paper sx={{ mt: 2 }}>
            <TableContainer>
              <Table aria-label="ipd discharge patient report table" sx={{ minWidth: 800 }}>
                <TableHead sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
                  <TableRow>
                    {visibleColumns.serialNumber && <TableCell>SNO</TableCell>}
                    {visibleColumns.patientId && <TableCell>Patient Id</TableCell>}
                    {visibleColumns.age && <TableCell>Age</TableCell>}
                    {visibleColumns.sex && <TableCell>Sex</TableCell>}
                    {visibleColumns.address && <TableCell>Address</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row, index) => (
                      <TableRow key={row.id || index}>
                        {visibleColumns.serialNumber && <TableCell>{page * rowsPerPage + index + 1}</TableCell>}
                        {visibleColumns.patientId && <TableCell>{row.patientId || "-"}</TableCell>}
                        {visibleColumns.age && <TableCell>{row.age || "-"}</TableCell>}
                        {visibleColumns.sex && <TableCell>{row.sex || "-"}</TableCell>}
                        {visibleColumns.address && <TableCell>{row.address || "-"}</TableCell>}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {currentData.length > 0 && (
              <TablePagination
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                component="div"
                count={currentData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Paper>
        );
      case 2:
        return (
          <Paper sx={{ mt: 2 }}>
            <TableContainer>
              <Table aria-label="gender wise ipd report table" sx={{ minWidth: 800 }}>
                <TableHead sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
                  <TableRow>
                    <TableCell>Gender</TableCell>
                    <TableCell>Count</TableCell>
                    <TableCell>Percentage</TableCell>
                    <TableCell>Avg Stay</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="text.secondary">
                          No breakdown data yet. (Mock series commented out; connect API when ready.)
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell>{row.gender}</TableCell>
                        <TableCell>{row.count}</TableCell>
                        <TableCell>{row.percentage}</TableCell>
                        <TableCell>{row.avgStay}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {currentData.length > 0 && (
              <TablePagination
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                component="div"
                count={currentData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Paper>
        );
      case 3:
        return (
          <Paper sx={{ mt: 2 }}>
            <TableContainer>
              <Table aria-label="unit wise ipd report table" sx={{ minWidth: 800 }}>
                <TableHead sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
                  <TableRow>
                    <TableCell>Unit</TableCell>
                    <TableCell>Discharges</TableCell>
                    <TableCell>Avg Stay</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="text.secondary">
                          No breakdown data yet. (Mock series commented out; connect API when ready.)
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell>{row.unit}</TableCell>
                        <TableCell>{row.discharges}</TableCell>
                        <TableCell>{row.avgStay}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {currentData.length > 0 && (
              <TablePagination
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                component="div"
                count={currentData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Paper>
        );
      case 4:
        return (
          <Paper sx={{ mt: 2 }}>
            <TableContainer>
              <Table aria-label="sponsor wise ipd report table" sx={{ minWidth: 800 }}>
                <TableHead sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold" } }}>
                  <TableRow>
                    <TableCell>Sponsor</TableCell>
                    <TableCell>Count</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="text.secondary">
                          No breakdown data yet. (Mock series commented out; connect API when ready.)
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell>{row.sponsor}</TableCell>
                        <TableCell>{row.count}</TableCell>
                        <TableCell>{row.amount}</TableCell>
                        <TableCell>{row.percentage}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {currentData.length > 0 && (
              <TablePagination
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                component="div"
                count={currentData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Paper>
        );
      default:
        return null;
    }
  };

  const getRowColumnCount = () => {
    if (!hasSearched) {
      return "Rows: 0 | Columns: 0";
    }
    const rows = currentData.length;
    let cols = 0;
    switch (activeTab) {
      case 1:
        cols = Object.values(visibleColumns).filter(v => v).length;
        break;
      case 2: cols = 4; break;
      case 3: cols = 3; break;
      case 4: cols = 4; break;
      default: cols = 0;
    }
    return `Rows: ${rows} | Columns: ${cols}`;
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          {/* Search and Buttons */}
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              IPD Discharge Patient Report
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              my: "20px",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => {
                    handleInputChange('searchTemplate', newValue);
                    // Clear error when field is changed
                    if (errors.includes('searchTemplate')) {
                      setErrors(errors.filter(e => e !== 'searchTemplate'));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('searchTemplate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  name="dischargeDate"
                  label="Discharge Date"
                  value={formData.dischargeDate}
                  onChange={(newValue) => {
                    handleInputChange('dischargeDate', newValue);
                    // Clear error when field is changed
                    if (errors.includes('dischargeDate')) {
                      setErrors(errors.filter(e => e !== 'dischargeDate'));
                    }
                  }}
                  slotProps={{
                    textField: { 
                      required: true, 
                      fullWidth: true,
                      InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                    },
                  }}
                  sx={{ 
                    bgcolor: "white",
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: errors?.includes('dischargeDate') ? '1px solid red' : '1px solid inherit',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>

          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              mb: "30px",
              mt: "20px",
              p: "20px",
            }}
          >
            {/* dynamic button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  border: "1px solid grey",
                  borderRadius: "5px",
                  px: 2,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  width: "900px",
                  cursor: "pointer",
                  mb: "20px",
                }}
              >
                <Typography
                  onClick={() => handleTabClick(1)}
                  sx={{
                    color: "grey",
                    textDecoration: "none",
                    cursor: "pointer",
                    ...(activeTab === 1 && {
                      color: "primary.main",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }),
                  }}
                >
                  IPD Discharge Patient Report
                </Typography>
                <Divider flexItem orientation="vertical" />
                <Typography
                  onClick={() => handleTabClick(2)}
                  sx={{
                    color: "grey",
                    textDecoration: "none",
                    cursor: "pointer",
                    ...(activeTab === 2 && {
                      color: "primary.main",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }),
                  }}
                >
                  Gender Wise IPD Report
                </Typography>
                <Divider flexItem orientation="vertical" />
                <Typography
                  onClick={() => handleTabClick(3)}
                  sx={{
                    color: "grey",
                    textDecoration: "none",
                    cursor: "pointer",
                    ...(activeTab === 3 && {
                      color: "primary.main",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }),
                  }}
                >
                  Unit Wise IPD Report
                </Typography>
                <Divider flexItem orientation="vertical" />
                <Typography
                  onClick={() => handleTabClick(4)}
                  sx={{
                    color: "grey",
                    textDecoration: "none",
                    cursor: "pointer",
                    ...(activeTab === 4 && {
                      color: "primary.main",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }),
                  }}
                >
                  Sponsor Wise IPD Report
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  mb: "20px",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}>
                  {getRowColumnCount()}
                </Typography>

                <Box sx={iconStyleProps} onClick={handleFilterClick}>
                  <EditIcon
                    sx={{
                      cursor: "pointer",
                      color: "transparent",
                      fontSize: "18px",
                    }}
                  />
                </Box>
                <Box sx={iconStyleProps} onClick={handleShare}>
                  <ShareIcon 
                    sx={{
                      cursor: "pointer",
                      fontSize: "18px",
                    }}
                  />
                </Box>
                <Box sx={iconStyleProps}>
                  <IconButton onClick={handleDownloadClick}>
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                </Box>
                <Menu
                  anchorEl={downloadAnchorEl}
                  open={downloadMenuOpen}
                  onClose={handleDownloadClose}
                  slotProps={{
                    sx: {
                      borderRadius: "12px",
                      boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                      padding: "8px 0",
                      width: "250px",
                    },
                  }}
                >
                  {menuItems.map((item, index) => (
                    <React.Fragment key={item}>
                      <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                        {item}
                      </MenuItem>
                      {index !== menuItems.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </Menu>
              </Box>
            </Box>

            {/* Tab Content */}
            {renderTabContent()}

            {/* Filter Menu for Column Visibility */}
            <Menu
              anchorEl={filterAnchorEl}
              open={filterMenuOpen}
              onClose={handleFilterClose}
              slotProps={{
                sx: {
                  borderRadius: "12px",
                  boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                  padding: "8px 0",
                  width: "200px",
                },
              }}
            >
              <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                Show/Hide Columns
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => toggleColumn('serialNumber')} sx={menuItemStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>SNO</Box>
                  {visibleColumns.serialNumber && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                </Box>
              </MenuItem>
              <MenuItem onClick={() => toggleColumn('patientId')} sx={menuItemStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>Patient Id</Box>
                  {visibleColumns.patientId && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                </Box>
              </MenuItem>
              <MenuItem onClick={() => toggleColumn('age')} sx={menuItemStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>Age</Box>
                  {visibleColumns.age && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                </Box>
              </MenuItem>
              <MenuItem onClick={() => toggleColumn('sex')} sx={menuItemStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>Sex</Box>
                  {visibleColumns.sex && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                </Box>
              </MenuItem>
              <MenuItem onClick={() => toggleColumn('address')} sx={menuItemStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>Address</Box>
                  {visibleColumns.address && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
